import { keepPreviousData, skipToken, useQuery } from "@tanstack/react-query";

import { AppointmentExchangeHistoryEntryResponse, appointmentExchangesApi } from "@/api";
import { QueryKey } from "@/query/Query";

import { ExchangeHistorySearchParams } from "../ExchangeHistoryPanel.types";

/** Stable fallback while the query has no data yet, so consumers' memo dependencies keep a fixed identity. */
const NO_ITEMS: AppointmentExchangeHistoryEntryResponse[] = [];

/**
 * The carried-out swaps for the server-side filters; nothing is requested while the filter form is invalid
 * (`null`). Nested under the exchanges query key on purpose: accepting an offer invalidates that prefix, so a
 * new swap shows up here without the mutation having to know about the history.
 */
export function useExchangeHistoryQuery(params: ExchangeHistorySearchParams | null) {
  const { data = NO_ITEMS, isLoading } = useQuery({
    queryKey: [
      QueryKey.AppointmentExchanges,
      "history",
      params?.fromDate?.getTime() ?? null,
      params?.tillDate?.getTime() ?? null,
      params?.givenAwayByCounselorId ?? null,
      params?.exchangedWithCounselorId ?? null,
    ],
    queryFn:
      params === null
        ? skipToken
        : () =>
            appointmentExchangesApi.getAppointmentExchangeHistory({
              from: params.fromDate ?? undefined,
              till: params.tillDate ?? undefined,
              givenAwayByCounselorId: params.givenAwayByCounselorId,
              exchangedWithCounselorId: params.exchangedWithCounselorId,
            }),
    // The filters apply live, so the list stays on screen while the next period or filter loads instead
    // of blanking on every keystroke.
    placeholderData: keepPreviousData,
  });

  return { data, isLoading };
}
